<template>
  <v-container fluid v-if="initialized">
    <div class="d-flex justify-space-between align-center">
      <h2>
        {{ $tc('location', 2) }}
        <vf-tooltip :text="$t('unresolved_locations_detected', { count: unregisteredLocationCount })" right>
          <v-btn
            v-if="unregisteredLocationCount > 0"
            fab text
            color="primary"
            class="ml-n1"
            to="/locations/unregistered">
            <v-badge :content="unregisteredLocationCount" overlap color="danger" offset-x="10" offset-y="10">
              <v-icon color="warning" large>mdi-alert-circle</v-icon>
            </v-badge>
          </v-btn>
        </vf-tooltip>
      </h2>
      <div>
        <v-btn
          v-if="userLoaded && user.can('create_location')"
          outlined
          color="primary"
          to="/location/create">
          <v-icon class="mr-1 ml-n2">mdi-plus</v-icon>
          {{ $t('new_location' )}}
        </v-btn>
        <upload-location-dialog
          v-if="userLoaded && user.can('upload_location')"
          @upload-complete="onUploadComplete">
          <template #activator="{ on, attrs }">
            <v-btn
              class="ml-2"
              outlined
              color="primary"
              v-on="on"
              v-bind="attrs">
              <v-icon class="mr-1 ml-n2">mdi-upload</v-icon>
              {{ $t('upload_locations') }}
            </v-btn>
          </template>
        </upload-location-dialog>
        <v-btn
          class="ml-2"
          outlined
          color="primary"
          to="/location/map">
          <v-icon class="mr-1 ml-n2">mdi-map-search</v-icon>
          {{ $t('location_map' )}}
        </v-btn>
      </div>
    </div>
    <!-- Search bar -->
    <v-sheet outlined rounded class="px-4 py-2">
      <div class="text-subtitle-1">{{ $t('filter_by') }}</div>
      <v-row dense>
        <v-col lg="4" md="6" cols="12">
          <v-text-field
            :label="$t('name')"
            v-model="searchCriteria.label"
            @keyup.enter="initiateSearch()"
            prepend-inner-icon="mdi-label">
          </v-text-field>
        </v-col>
        <v-col lg="4" md="6" cols="12">
          <v-text-field
            :label="$tc('code')"
            v-model="searchCriteria.code"
            @keyup.enter="initiateSearch()"
            prepend-inner-icon="mdi-barcode">
          </v-text-field>
        </v-col>
        <v-col lg="4" md="6" cols="12">
          <vf-autocomplete
            small
            :label="$t('location_type')"
            v-model="searchCriteria.locationTypes"
            :items="locationTypeOptions"
            prepend-inner-icon="mdi-filter"/>
        </v-col>
      </v-row>
      <div class="d-flex justify-end mt-1 mb-2">
        <v-btn outlined color="primary" @click="initiateSearch()" :loading="remoteData.isBusy">
          <v-icon class="mr-1 ml-n1">mdi-magnify</v-icon>
          {{ $t('search') }}
        </v-btn>
        <v-btn outlined color="primary" class="ml-2" @click="resetTable()" :disabled="tableControlsMatchDefaults">
          <v-icon class="mr-1 ml-n1">mdi-filter-remove</v-icon>
          {{ $t('reset') }}
        </v-btn>
      </div>
    </v-sheet>
    <div class="d-flex justify-space-between align-center mt-2">
      <div class="text-subtitle-1 font-weight-bold hidden-md-and-down">{{ $tc('result', 2) }}</div>
      <table-pagination-controls
        :total-rows="remoteData.totalRows"
        :current-page.sync="remoteData.currentPage"
        :rows-per-page.sync="remoteData.rowsPerPage"
        @update:currentPage="refreshResults"
        @update:rowsPerPage="initiateSearch"
        :per-page-options="remoteData.pageOptions"/>
      <div>
        <v-btn
          outlined
          color="primary"
          class="hidden-md-and-down ml-2"
          @click="downloadRemoteDataCsv()"
          :loading="remoteData.isDownloading">
          <v-icon class="mr-1 ml-n1">mdi-tray-arrow-down</v-icon>
          {{ $t('download') }}
        </v-btn>
        <vf-tooltip :text="$t('download')">
          <v-btn class="hidden-lg-and-up" fab text color="primary" @click="downloadRemoteDataCsv()" :loading="remoteData.isDownloading">
            <v-icon>mdi-tray-arrow-down</v-icon>
          </v-btn>
        </vf-tooltip>
      </div>
    </div>
    <div class="data-table-wrapper" :class="{'loading': remoteData.isBusy}">
      <v-data-table
        hide-default-footer
        class="vf-table mt-2"
        :headers="tableFields"
        :items="remoteData.items"
        :server-items-length="remoteData.totalRows"
        :sort-by.sync="remoteData.sortBy"
        :sort-desc.sync="remoteData.sortDesc"
        :loading="remoteData.isBusy"
        :loading-text="$t('loading')"
        @update:sort-by="initiateSearch()"
        @update:sort-desc="initiateSearch()">
        <template #item.label="row">
          <if-set :value="row.item.label">
            <router-link :to="`/location/update/${row.item.id}`">
              {{ row.value }}
            </router-link>
          </if-set>
        </template>
        <template #item.code="row">
          <if-set :value="row.value" dash>
            {{ row.value }}
          </if-set>
        </template>
        <template #item.location="row">
          <if-set :value="row.item.hasGpsCoordinates()" dash>
            <a :href="`https://maps.google.com/maps?q=${row.item.getGpsCoordinatesAsString()}`">
              <v-icon color="primary">mdi-map-marker</v-icon>
              {{ row.item.getGpsCoordinatesAsString() }}
            </a>
          </if-set>
        </template>
        <template #item.geofence="row">
          <span v-if="row.item.geofence_type === 'circle'">
            {{ customerDistance(row.item.getRadius(customerDefaultRadius)) }}
            {{ $t(`${radiusUnitOfMeasure}_abbreviated`) }}
          </span>
          <span v-else>
            {{ $tc(row.item.geofence_type) }}
          </span>
        </template>
        <template #item.type="row">
          <v-icon>{{ row.item.vuetifyIcon }}</v-icon>
          {{ $tc(`${row.value}_location_type`) }}
        </template>
        <template v-slot:item.edit="row">
          <router-link :to="`/location/update/${row.item.id}`">
            <v-icon small>mdi-pencil</v-icon>
          </router-link>
        </template>
      </v-data-table>
    </div>
  </v-container>
  <v-container v-else>
    <index-table-skeleton>
      <template v-slot:header>
        <h2>{{ $tc('location', 2)}}</h2>
      </template>
    </index-table-skeleton>
  </v-container>
</template>

<script>
import IndexTableSkeleton from '@/components/shared/IndexTableSkeleton'
import TablePaginationControls from '@/components/shared/TablePaginationControls'
import VfTooltip from '@/components/shared/VfTooltip'
import VfAutocomplete from '@/components/shared/VfAutocomplete'
import IfSet from '@/components/shared/IfSet'
import UploadLocationDialog from '@/components/location/UploadLocationDialog'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import RemoteDataTableMixin from '@/components/mixins/RemoteDataTableMixin'
import BrowserUtilitiesMixin from '@/components/mixins/BrowserUtilitiesMixin'
import LocationUtilitiesMixin from '@/components/location/LocationUtilitiesMixin'
import QueryParamMixin from '@/components/mixins/QueryParamMixin'
import { map, some } from 'lodash'

export default {
  name: 'LocationIndex',
  components: {
    IndexTableSkeleton,
    TablePaginationControls,
    VfTooltip,
    VfAutocomplete,
    IfSet,
    UploadLocationDialog
  },
  mixins: [
    CurrentUserMixin,
    RemoteDataTableMixin,
    BrowserUtilitiesMixin,
    LocationUtilitiesMixin,
    QueryParamMixin
  ],
  data () {
    return {
      model: 'Location',
      locationTypeOptions: [],
      locationTypesLoaded: false,
      searchCriteria: this.getDefaultSearchCriteria(),
      queryParams: [
        {
          name: 'label',
          type: String
        },
        {
          name: 'code',
          type: String
        },
        {
          name: 'locationTypes',
          type: Array,
          validator: value => some(this.locationTypeOptions, x => x.value === value)
        }
      ]
    }
  },
  computed: {
    /**
     * Whether the options have been loaded yet
     * @returns boolean
     */
    initialized () {
      return this.locationTypesLoaded
    },
    unregisteredLocationCount: function () {
      return this.$store.getters['sideNavStateModule/unregisteredLocationCount']
    },
    tableFields () {
      const fields = [
        {
          value: 'label',
          text: this.$tc('name'),
          sortable: true
        },
        {
          value: 'code',
          text: this.$tc('code'),
          sortable: true
        },
        {
          value: 'location',
          text: this.$tc('gps'),
          sortable: false
        },
        {
          value: 'geofence',
          text: this.$tc('geofence'),
          sortable: false
        },
        {
          value: 'type',
          text: this.$tc('type'),
          sortable: false
        },
        {
          text: this.$t('edit'),
          value: 'edit',
          sortable: false
        }
      ]
      if (this.userLoaded && this.user.can('view_rootentity')) {
        fields.splice(2, 0, {
          value: 'primary_customer.name',
          text: this.$t('primary_customer'),
          class: 'root-only',
          cellClass: 'root-only'
        })
      }
      return fields
    }
  },
  methods: {
    /**
     * Sets our data table back to its original state before the user applied filtering and paging changes.
     * @return undefined
     */
    resetTable () {
      this.searchCriteria = this.getDefaultSearchCriteria()
      this.resetRemoteDataTableNavigation()
      this.resetQueryParams()
      this.refreshResults()
    },
    /**
     * Returns the default search criteria for this data table (DRY)
     * @return Object
     */
    getDefaultSearchCriteria () {
      return {
        label: null,
        code: null,
        locationTypes: []
      }
    },
    /**
     * Constructs an object of key value pairs that represent the current search criteria
     * @return object
     */
    buildSearchCriteria () {
      const searchCriteria = {
        customer_id: this.customerContext.id,
        expand: 'service_provider,route,primary_customer',
        fields: 'id,label,code,primary_customer.name,latitude,longitude,type,geofence',
        page: this.remoteData.currentPage,
        'per-page': this.remoteData.rowsPerPage
      }
      if (this.searchCriteria.label != null) {
        searchCriteria.label = this.searchCriteria.label
      }
      if (this.searchCriteria.code != null) {
        searchCriteria.code = this.searchCriteria.code
      }
      if (this.searchCriteria.locationTypes.length > 0) {
        searchCriteria.type = this.searchCriteria.locationTypes.join(',')
      }
      if (this.remoteData.sortBy !== null) {
        const sortDirection = this.remoteData.sortDesc ? '-' : ''
        searchCriteria.sort = `${sortDirection}${this.remoteData.sortBy}`
      }
      return searchCriteria
    },
    async fetchLocationTypes () {
      this.locationTypesLoaded = false
      try {
        const response = await this.$orm('LocationType').findAll({})
        this.locationTypeOptions = map(response.getItems(), type => {
          return {
            text: this.$t(`${type.code}_location_type`),
            value: type.code
          }
        })
        this.locationTypesLoaded = true
      } catch (error) {
        this.handleError(error)
      }
    },
    /**
     * Handle upload completion - refresh the table
     */
    onUploadComplete () {
      this.refreshResults()
    }
  },
  mounted () {
    this.updateBrowserTitle(this.$tc('location', 2))
    this.initiateSearch()
    this.fetchLocationTypes()
    this.$store.dispatch('sideNavStateModule/refreshUnregisteredLocationCount', this.customerContext.id)
  },
  watch: {
    initialized () {
      this.useQueryParamsForSearch()
      this.initiateSearch()
    }
  }
}
</script>
